import React from 'react';
import WikiIcon from '../../shared/WikiIcon';

/**
 * "How people feel, by topic" (E2 §2.1 item 2): header + mood legend and the
 * six-column topic table (Topic · Questions · Mood · Trend · Corrections ·
 * Gaps) with a signal line + "Investigate" cue under every row. The whole row
 * is the click target for everyone (Editors get the denial toast from the
 * parent), exactly like the prototype.
 */

/** Prototype `moodCols` / `brMoodLegend`, used when the payload has no legend. */
export const MOOD_LEGEND = Object.freeze([
    { id: 'calm', label: 'Calm or focused', color: '#CBD5E1' },
    { id: 'hurried', label: 'Hurried', color: '#FCD34D' },
    { id: 'frustrated', label: 'Frustrated', color: '#F87171' },
    { id: 'confused', label: 'Confused', color: '#C4B5FD' },
    { id: 'anxious', label: 'Anxious', color: '#FDBA74' },
]);

const EMOTION_OFF_NOTE = {
    setting: 'Moods are hidden because "Read how people feel" is off in Settings.',
    plan: 'Moods are hidden because your plan doesn\'t include emotion insights.',
};

function MoodBar({ mood, legend }) {
    const parts = Array.isArray(mood) ? mood : [];
    return (
        <div className="wk-bri-mood" data-testid="brain-topic-mood" data-empty={parts.length ? undefined : 'true'}>
            {parts.map((value, index) => {
                const width = Math.max(0, Math.min(100, Number(value) || 0));
                if (!width) return null;
                return (
                    <div
                        key={legend[index]?.id || index}
                        className="wk-bri-seg"
                        style={{ width: `${width}%`, background: legend[index]?.color || '#CBD5E1' }}
                        title={`${legend[index]?.label || ''} ${width}%`.trim()}
                    />
                );
            })}
        </div>
    );
}

export default function TopicTable({ topics = [], legend, emotion, windowLabel, onOpen }) {
    const moods = Array.isArray(legend) && legend.length ? legend : MOOD_LEGEND;
    const offNote = emotion && emotion.enabled === false ? EMOTION_OFF_NOTE[emotion.reason] : null;

    const onKeyDown = (event, topic) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen(topic);
        }
    };

    return (
        <section className="wk-bri-topics" aria-label="How people feel, by topic">
            <div className="wk-bri-head">
                <h3 className="wk-t-h">How people feel, by topic</h3>
                <span className="wk-t-meta">{windowLabel || 'This week'} · grouped by topic, never by person</span>
                <span className="wk-spacer" />
                <div className="wk-bri-legend" data-testid="brain-mood-legend">
                    {moods.map((mood) => (
                        <span key={mood.id || mood.label} className="wk-bri-legend-item">
                            <span className="wk-bri-swatch" style={{ background: mood.color }} aria-hidden="true" />
                            {mood.label}
                        </span>
                    ))}
                </div>
            </div>
            {offNote && <p className="wk-t-meta wk-bri-off" data-testid="brain-emotion-off">{offNote}</p>}
            {topics.length === 0 ? (
                <div className="wk-bro-empty" data-testid="brain-topics-empty">
                    No questions this week yet. Topics appear here once people ask the Wiki.
                </div>
            ) : (
                <div className="wk-bri-table" data-testid="brain-topic-table">
                    <div className="wk-bri-table-inner">
                        <div className="wk-bri-cols wk-bri-thead wk-t-label" aria-hidden="true">
                            <span>Topic</span>
                            <span>Questions</span>
                            <span>Mood</span>
                            <span>Trend</span>
                            <span>Corrections</span>
                            <span>Gaps</span>
                        </div>
                        {topics.map((topic) => (
                            <div
                                key={topic.id}
                                className="wk-bri-row"
                                role="button"
                                tabIndex={0}
                                onClick={() => onOpen(topic)}
                                onKeyDown={(event) => onKeyDown(event, topic)}
                                aria-label={`${topic.name}. ${topic.questions ?? 0} questions, ${topic.trend || ''}. Investigate`}
                                data-testid="brain-topic-row"
                                data-topic-id={topic.id}
                            >
                                <div className="wk-bri-cols wk-bri-cols--row">
                                    <div className="wk-bri-topic">
                                        <div className="wk-t-name wk-ellipsis">{topic.name}</div>
                                        {topic.category_name && (
                                            <div className="wk-t-meta wk-ellipsis">{topic.category_name}</div>
                                        )}
                                    </div>
                                    <span className="wk-bri-num">{topic.questions ?? 0}</span>
                                    <div><MoodBar mood={topic.mood} legend={moods} /></div>
                                    <span
                                        className={`wk-bri-trend${topic.bad ? ' wk-bri-trend--bad' : ''}`}
                                       
                                        data-testid="brain-topic-trend"
                                    >
                                        {topic.trend}
                                    </span>
                                    <span className="wk-bri-num">{topic.corrections ?? 0}</span>
                                    <span className="wk-bri-num">{topic.gaps ?? 0}</span>
                                </div>
                                <div className="wk-bri-signal">
                                    <WikiIcon name="info" size={13} className="wk-bri-signal-icon" />
                                    <span className="wk-bri-signal-text">{topic.signal}</span>
                                    <span className="wk-bri-investigate">Investigate</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}
